import { TypedDocumentNode, gql } from '@apollo/client';

import {
  SortingOption,
  Sport,
} from '@sorare/core/src/__generated__/globalTypes';
import { PullToRefetch } from '@sorare/core/src/atoms/navigation/PullToRefetch';
import { extractConnectionData } from '@sorare/core/src/gql/extractData';
import usePaginatedQuery from '@sorare/core/src/hooks/graphql/usePaginatedQuery';

import { Sale } from 'components/sale/Sale';

import DisplayItems from '../../common/DisplayItems';
import { DefaultSortType } from '../../common/types';
import {
  NoBuyerSaleOffersQuery,
  NoBuyerSaleOffersQueryVariables,
} from './__generated__/index.graphql';

const NO_BUYER_SALE_OFFERS_QUERY = gql`
  query NoBuyerSaleOffersQuery(
    $cursor: String
    $sortByEndDate: SortingOption
    $sport: [Sport!]
    $onlyPrimary: Boolean = false
  ) {
    currentUser {
      slug
      endedWithNoBuyerSingleSaleTokenOffers(
        first: 10
        after: $cursor
        sortByEndDate: $sortByEndDate
        sport: $sport
      ) {
        totalCount
        nodes {
          id
          ...Sale_offer
        }
        pageInfo {
          endCursor
          hasNextPage
        }
      }
    }
  }
  ${Sale.fragments.offer}
` as TypedDocumentNode<NoBuyerSaleOffersQuery, NoBuyerSaleOffersQueryVariables>;

const NoBuyerSingleSaleOffers = ({
  sortType,
  sport,
}: {
  sortType: DefaultSortType;
  sport: Sport[];
}) => {
  const sortByEndDate =
    sortType === DefaultSortType.LATEST
      ? SortingOption.DESC
      : SortingOption.ASC;
  const { loading, data, loadMore, refetch } = usePaginatedQuery(
    NO_BUYER_SALE_OFFERS_QUERY,
    {
      notifyOnNetworkStatusChange: true,
      connection: 'TokenOfferConnection',
      variables: {
        sortByEndDate,
        sport,
      },
    }
  );

  const { items, count, cursor, hasMore } = extractConnectionData(
    data?.currentUser?.endedWithNoBuyerSingleSaleTokenOffers,
    p => <Sale key={p.id} sale={p} />
  );
  const displayLoading = !items && loading;

  return (
    <PullToRefetch refetch={refetch}>
      <DisplayItems
        displayLoading={displayLoading}
        count={count}
        items={items}
        loadMoreSection={{
          hasMore,
          loading,
          loadMore: () => {
            loadMore(false, {
              cursor,
              sortByEndDate,
              sport,
            });
          },
        }}
      />
    </PullToRefetch>
  );
};

export default NoBuyerSingleSaleOffers;
